/**
 * French translations
 */
import type { TranslationKeys } from '../utils/i18n';

export const fr: TranslationKeys = {
  controls: {
    maxTime: 'Temps Maximum',
    maxTimeMinutes: 'Temps Maximum (minutes)',
    travelMode: 'Mode de Voyage',
    speed: 'Vitesse',
    mapStyle: 'Style de Carte',
    language: 'Langue',
    share: 'Partager',
    play: 'Lire',
    pause: 'Pause',
    restart: 'Redémarrer',
    loading: 'Chargement...',
    showControls: 'Afficher les Contrôles',
    hideControls: 'Masquer les Contrôles',
    expandControls: 'Développer les contrôles',
    collapseControls: 'Réduire les contrôles',
    selectLocationHint: 'Veuillez sélectionner un emplacement sur la carte pour commencer',
  },
  travelModes: {
    driving: 'Conduite',
    walking: 'Marche',
    cycling: 'Vélo',
    transit: 'Transit',
  },
  speed: {
    verySlow: '0.1x (Très Lent)',
    slow: '0.25x (Lent)',
    half: '0.5x (Moitié)',
    normal: '1x (Normal)',
    fast: '1.5x (Rapide)',
    veryFast: '2x (Très Rapide)',
  },
  mapStyles: {
    streets: 'Rues',
    satellite: 'Satellite',
  },
  languages: {
    en: 'English',
    'zh-Hans': '简体中文 (Simplified Chinese)',
    'zh-Hant': '繁體中文 (Traditional Chinese)',
    es: 'Español (Spanish)',
    fr: 'Français (French)',
    de: 'Deutsch (German)',
    ja: '日本語 (Japanese)',
    ko: '한국어 (Korean)',
  },
  addressInput: {
    label: 'Rechercher une Adresse',
    placeholder: 'Entrez une adresse pour calculer la carte de temps',
    noResults: 'Aucun résultat trouvé',
    loading: 'Chargement des suggestions',
  },
  error: {
    retry: 'Réessayer',
    dismiss: 'Ignorer l\'erreur',
    somethingWentWrong: 'Quelque chose s\'est mal passé. Veuillez actualiser la page.',
    browserNotSupported: 'Votre navigateur ne prend pas en charge cette application. Veuillez utiliser un navigateur moderne.',
    usageLimitReached: 'Vous avez atteint la limite bêta de 5 calculs. Merci d\'avoir essayé ! Envoyez-moi un DM si vous êtes intéressé par ce projet.',
  },
  progress: {
    minutes: 'min',
  },
  share: {
    urlCopied: 'URL copiée dans le presse-papiers !',
    shareUrl: 'Partager l\'URL',
  },
  emailGate: {
    title: 'Connectez-vous à Carte Temporelle Beta',
    subtitle: 'Entrez votre adresse e-mail pour recevoir un lien de connexion',
    emailPlaceholder: 'your.email@example.com',
    emailLabel: 'Adresse e-mail',
    sendButton: 'Envoyer le Lien de Connexion',
    sendingButton: 'Envoi du lien de connexion...',
    checkEmailTitle: 'Vérifiez votre e-mail',
    checkEmailMessage: 'Nous avons envoyé un lien de connexion à {email}',
    checkEmailInstruction: 'Cliquez sur le lien dans l\'e-mail pour continuer vers la carte temporelle.',
    checkEmailHelp: 'Vous n\'avez pas reçu l\'e-mail ? Vérifiez votre dossier spam ou réessayez avec une autre adresse e-mail.',
    emailRequired: 'Veuillez entrer votre adresse e-mail',
    emailInvalid: 'Veuillez entrer une adresse e-mail valide',
    domainRestriction: 'Nous n\'autorisons que les adresses e-mail @gmail.com, @outlook.com et @qq.com pour l\'accès bêta.',
    domainNotAllowed: 'L\'accès bêta est actuellement limité aux adresses e-mail Gmail, Outlook et QQ',
    typoSuggestion: 'Vouliez-vous dire @{domain} ?',
    rateLimitExceeded: 'Trop de requêtes. Veuillez attendre un moment et réessayer.',
    invalidEmailFormat: 'Adresse e-mail invalide. Veuillez vérifier et réessayer.',
    failedToSend: 'Échec de l\'envoi du lien de connexion. Veuillez réessayer.',
    magicLinkFailed: 'Échec de l\'envoi du lien magique : {error}',
    testMeButton: 'Testez-moi',
    bannerText: 'Carte Temporelle',
    betaTag: 'Beta',
  },
  testCode: {
    title: 'Entrez le code de test',
    subtitle: 'Vous avez un code de test ? Entrez-le ci-dessous pour commencer',
    placeholder: 'Entrez le code',
    button: 'Continuer avec le code',
    haveCodeLink: 'Vous avez un code de test ?',
    useEmailLink: 'Utiliser l\'email à la place',
    invalid: 'Code de test invalide',
    expired: 'Ce code de test a expiré',
    limitReached: 'Ce code de test a atteint sa limite d\'utilisation',
    optionalEmailTitle: 'Enregistrer votre progression (Facultatif)',
    optionalEmailSubtitle: 'Voulez-vous enregistrer votre travail ? Entrez votre email ci-dessous (ou ignorer pour l\'instant)',
    optionalEmailPlaceholder: 'votre.email@exemple.com (facultatif)',
    skipButton: 'Ignorer pour l\'instant',
    submitEmailButton: 'Continuer',
  },
  usageIndicator: {
    template: '{count} calculs restants',
    loading: 'Chargement...',
  },
  disclaimer: {
    loading: 'Chargement...',
    title: 'Avertissement Important',
    content: 'Ceci est un projet bêta et expérimental. Toutes les données cartographiques sont fournies par Mapbox, un service tiers, et n\'ont pas été vérifiées de manière indépendante. Les cartes peuvent contenir des inexactitudes et ne doivent pas être utilisées pour des décisions critiques. Les calculs de temps de trajet sont des estimations uniquement et n\'ont pas été vérifiés pour leur exactitude. Nous n\'assumons aucune responsabilité pour les inexactitudes, erreurs ou conséquences résultant de l\'utilisation de cette application.',
    consent: 'En continuant à utiliser cette application, vous reconnaissez et acceptez tous les termes et avis de non-responsabilité ci-dessus.',
  },
  intro: {
    title: 'Découvrez Votre Portée',
    message: 'Visualisez jusqu\'où vous pouvez voyager dans un temps donné depuis n\'importe quel endroit.\n\nVoyez votre accessibilité en temps de trajet animée sur la carte pour prendre de meilleures décisions sur où vivre, travailler ou visiter.',
    step1: 'Recherchez ou cliquez sur la carte pour définir votre point de départ',
    step2: 'La carte montre votre accessibilité en temps de trajet avec des zones animées',
    step3: 'Configurez votre temps de trajet et votre mode de transport',
    step4: 'Cliquez sur Lecture pour voir votre portée s\'étendre au fil du temps',
    step5: 'Partagez votre visualisation avec d\'autres via un lien partageable',
    next: 'Suivant',
    skip: 'Passer la visite',
    gotIt: 'Compris !',
    startTour: 'Commencer la visite',
  },
};

